import type { ReactNode } from "react";
import type {
  ComparisonOperatorsType,
  LogicalOperatorsType,
} from "@validereinc/utilities";
import { InputTypes } from "../Form/FormHelpers/types";

/**
 * Primitive input types supported by this filter.
 * This type only describes input components; for example "string-input" is <TextInput ... />
 * and "picklist-multi-input" is <Dropdown isMulti ... />
 */
export type FilterBuilderInputType =
  | "string-input"
  | "number-input"
  | "boolean-input"
  | "picklist-single-input"
  | "date-input"
  | "picklist-multi-input"
  | "list-input"
  | "custom-pick-list-input";

/**
 * Type of supported conditions in this component
 */
export type FilterBuilderOperators = "=" | ComparisonOperatorsType;

// Types supported by this filter:
export const FilterBuilderSupportedDataTypes = {
  STRING: InputTypes.STRING,
  NUMBER: InputTypes.NUMBER,
  INTEGER: InputTypes.INTEGER,
  PICK_LIST: InputTypes.PICK_LIST,
  DATE: InputTypes.DATE,
  BOOLEAN: InputTypes.BOOLEAN,
  MIXED: "mixed",
  CUSTOM_PICK_LIST: "custom-pick-list",
} as const;

export type FilterBuilderSupportedDataTypesType =
  (typeof FilterBuilderSupportedDataTypes)[keyof typeof FilterBuilderSupportedDataTypes];

/**
 * "date-fns"-compatible types that are supported by FilterBuilder
 */
export type SupportedDateInputFormats = "yyyy" | "yyyyMM" | "yyyy-MM-dd";

/**
 * Type of a single field this component works with:
 */
export type FilterBuilderFieldDefinition =
  | {
      data_type: "string";
      display_name: string;
    }
  | {
      data_type: "number";
      display_name: string;
    }
  | {
      data_type: "integer";
      display_name: string;
    }
  | {
      data_type: "pick-list";
      display_name: string;
      pick_list_options: string[];
      /**
       * Optional mapping from option value to display label.
       * When provided, the label for each option will be looked up from this map.
       * When absent, the option value itself is used as the label.
       */
      pick_list_labels?: Record<string, string>;
    }
  | {
      data_type: "date";
      display_name: string;
      format: SupportedDateInputFormats;
    }
  | {
      data_type: "boolean";
      display_name: string;
    }
  | {
      data_type: "mixed";
      display_name: string;
    }
  | {
      data_type: "custom-pick-list";
      display_name: string;
      renderInput: (props: FilterBuilderCustomRenderInputProps) => ReactNode;
    };

/**
 * Props passed to a custom render function for custom pick lists
 */
export type FilterBuilderCustomRenderInputProps = {
  /** Current filter value; string for single-select operators ($eq, $ne), string[] for multi-select ($in, $all). */
  value: string | string[];
  /** Callback to update the filter value when the user selects a new value. */
  onChange: (newValue: string | string[]) => void;
  /** Whether the input should be disabled. */
  isDisabled?: boolean;
  /** True when the selected operator is "Is one of" ($in) or "Has all of" ($all); use for multi-select dropdowns. */
  isMulti: boolean;
};

/**
 * Type of "fields" prop this component takes:
 */
export type FilterBuilderFieldsType = Record<
  string,
  FilterBuilderFieldDefinition
>;

/**
 * Type of configuration about which data type can have which operators.
 */
export type FilterBuilderAllowedOperatorsMapping = Record<
  FilterBuilderSupportedDataTypesType,
  Partial<Record<FilterBuilderOperators, FilterBuilderInputType>>
>;

export type FilterBuilderRecursionDataType = {
  prefixKey: Array<string | number>;
  depth: number;
  currentJoinOperator: Exclude<LogicalOperatorsType, "$not"> | "";
};

export type FilterBuilderFilterType = any;

/**
 * Input modes for FilterBuilder value inputs
 */
export const FilterBuilderInputModes = {
  TYPED_VALUE: "typed_value",
  VARIABLE: "variable",
} as const;

export type FilterBuilderInputModesType =
  (typeof FilterBuilderInputModes)[keyof typeof FilterBuilderInputModes];

export type FilterBuilderProps = {
  /** Object value of the filter. e.g. `{ "name": "John", "status":{$in: ["active", "inactive"] } }` */
  filter: FilterBuilderFilterType;
  /**
   * (ATTN: This function only takes the setState function and doesn't return the new filter value. For getting triggers on when a filter value changes, use onFilterChange)
   *
   * State-change function that sets the new filter.
   *
   * e.g. given: `[state, setState] = useState({})`
   *
   * You need to pass `setState` to this prop.
   * */
  setFilter: React.Dispatch<React.SetStateAction<FilterBuilderFilterType>>;

  /**
   * This optional function is called when a filter value is changed.
   * Don't use this prop as the `setState` function for mutating filter state. Pass that function to `setFilter`.
   *
   * @param newValue New filter value after a change is made.
   */
  onFilterChange?: (newValue: FilterBuilderFilterType) => void;
  /** Definition of the fields that can be used in the filter builder. */
  fields: FilterBuilderFieldsType;
  /** Indicates the maximum depth of the filter; 0 means no nesting of `$and`s and `$or`s is allowed. */
  maxDepth?: number;
  /** Title of the filter builder. */
  title?: string;
  /** Whether the filter builder is disabled. */
  isDisabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  /** Variant of the filter builder. `vertical` means that the filter builder will be displayed vertically. */
  variant?: "vertical" | "default";
  /** When this prop is set, the field picker will be disabled and the field with this key will be selected by default.
   * It's used for scenarios where only one field is available and filter builder is supposed to set the value of that single field. */
  defaultSingleField?: string;
  /**
   * Available variables that can be used in variable mode
   */
  availableVariables?: string[];
  /**
   * Whether to show the input mode toggle (typed value vs variable)
   * @default true
   */
  showInputModeToggle?: boolean;
  /** Internal prop that is used to track the recursion depth and the current join operator. Do not set manually. */
  _recursion?: FilterBuilderRecursionDataType;
  /**
   * Operator mapping overrides to use instead of the defaults
   * @default undefined
   */
  operatorMappingOverride?: FilterBuilderAllowedOperatorsMapping;
};
